import { useEffect, useRef } from "react";

type LiquidMaterial = {
  metalness: number;
  roughness: number;
  map?: unknown;
  emissive?: { set: (hex: number) => void };
  emissiveMap?: unknown;
  emissiveIntensity?: number;
  color?: { set: (hex: number) => void };
  needsUpdate?: boolean;
};

type LiquidApp = {
  loadImage: (url: string) => void;
  liquidPlane: {
    material: LiquidMaterial;
    uniforms: {
      displacementScale: { value: number };
      [key: string]: { value: unknown };
    };
  };
  setRain: (v: boolean) => void;
  dispose?: () => void;
};

declare global {
  interface Window {
    __liquidLoader?: Promise<(el: HTMLCanvasElement) => LiquidApp>;
  }
}

function loadLiquid(): Promise<(el: HTMLCanvasElement) => LiquidApp> {
  if (!window.__liquidLoader) {
    window.__liquidLoader = import(
      /* @vite-ignore */
      "https://cdn.jsdelivr.net/npm/threejs-components@0.0.27/build/backgrounds/liquid1.min.js"
    ).then((mod: { default: (el: HTMLCanvasElement) => LiquidApp }) => mod.default);
  }
  return window.__liquidLoader;
}

const BACKGROUND_IMAGE_URL =
  "https://storage.readdy-site.link/project_files/b174a1d9-9377-435c-a2ea-cfe40f75d252/317870b9-05c9-4138-9e14-618746746add_c52805593bcdcc6880b9f4e9a0000519.png?v=561ad8f491204bdf47a2a49e8b627e30";

export default function LiquidCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const appRef = useRef<LiquidApp | null>(null);
  const rainIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const canvas = canvasRef.current;
    if (!canvas) return;

    loadLiquid()
      .then((LiquidBackground) => {
        if (cancelled || !canvas) return;
        const app = LiquidBackground(canvas);
        appRef.current = app;

        // Load the background image as the liquid texture
        app.loadImage(BACKGROUND_IMAGE_URL);

        // Kill the chrome reflection / halo so the image shows clean
        app.liquidPlane.material.metalness = 0;
        app.liquidPlane.material.roughness = 1;

        // Subtle displacement so ripples warp the image gently
        app.liquidPlane.uniforms.displacementScale.value = 3;

        // Tweak any rain/ripple-related uniforms to be much calmer
        // so the surface feels like a quiet pond instead of heavy rain.
        const u = app.liquidPlane.uniforms as Record<string, { value: unknown }>;
        if (u.rainStrength) u.rainStrength.value = 0.12;
        if (u.uRainStrength) u.uRainStrength.value = 0.12;
        if (u.dropStrength) u.dropStrength.value = 0.12;
        if (u.rippleStrength) u.rippleStrength.value = 0.12;
        if (u.rainAmount) u.rainAmount.value = 0.15;
        if (u.rainFrequency) u.rainFrequency.value = 0.15;
        if (u.dropFrequency) u.dropFrequency.value = 0.15;
        if (u.rainSpeed) u.rainSpeed.value = 0.5;

        // Enable rain so the surface gets continuous (but very gentle) drops.
        // We also throttle the internal drop spawner via a setInterval-style
        // override below by replacing setRain with a low-cadence toggle.
        app.setRain(true);

        // Some builds of this library spawn drops every frame at full rate.
        // We dampen them by quickly toggling rain off after each tick so only
        // a small fraction of drops actually get added.
        let rainTick = 0;
        const rainInterval = window.setInterval(() => {
          rainTick = (rainTick + 1) % 6;
          // Only let rain be "on" 1 out of every 6 ticks → very sparse drops
          app.setRain(rainTick === 0);
        }, 350);
        rainIntervalRef.current = rainInterval;

        // Turn OFF directional/point lights entirely (they cause the hotspot)
        // and crank ambient to 1 so the texture shows its true colors.
        try {
          const plane = app.liquidPlane as unknown as {
            parent?: { traverse?: (cb: (obj: unknown) => void) => void };
          };
          plane.parent?.traverse?.((obj: unknown) => {
            const o = obj as {
              isLight?: boolean;
              isAmbientLight?: boolean;
              intensity?: number;
              color?: { set: (hex: number) => void };
              visible?: boolean;
            };
            if (o.isLight) {
              if (o.isAmbientLight) {
                o.intensity = 1;
                o.color?.set?.(0xffffff);
              } else {
                o.intensity = 0;
                o.visible = false;
              }
            }
          });
        } catch {
          // noop
        }

        // Make sure the material uses the image at true colors
        const mat = app.liquidPlane.material;
        mat.emissiveMap = null;
        if (mat.emissive?.set) mat.emissive.set(0x000000);
        mat.emissiveIntensity = 0;
        if (mat.color?.set) mat.color.set(0xffffff);
        mat.needsUpdate = true;
      })
      .catch((err) => {
        console.error("Failed to load liquid background", err);
      });

    return () => {
      cancelled = true;
      if (rainIntervalRef.current) {
        window.clearInterval(rainIntervalRef.current);
        rainIntervalRef.current = null;
      }
      if (appRef.current?.dispose) {
        try {
          appRef.current.dispose();
        } catch {
          // noop
        }
      }
      appRef.current = null;
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      id="canvas"
      className="absolute inset-0 w-full h-full block"
    />
  );
}